import React, { useEffect, useMemo, useState } from "react";
import { api, apiErr } from "@/lib/api";
import PortalLayout from "@/components/PortalLayout";
import { Button } from "@/components/ui/Button";
import { toast } from "sonner";
import {
  Loader2, X, ChevronRight, CheckCircle2, XCircle, KeyRound,
} from "lucide-react";

const STATUS_META = {
  submitted:        { label: "În analiză Manager", fg: "#a16207", bg: "#fef9c3" },
  manager_approved: { label: "În analiză PM",     fg: "#1d4ed8", bg: "#dbeafe" },
};
const ACCESS_ACTION_LABEL = {
  create:           "Cerere creată",
  submit:           "Trimisă spre aprobare",
  manager_approve:  "Aprobată de Manager Superior",
  manager_reject:   "Respinsă de Manager Superior",
  pm_approve:       "Aprobată de Project Manager",
  pm_reject:        "Respinsă de Project Manager",
  request_info:     "Cerere de informații suplimentare",
  provide_info:     "Informații suplimentare furnizate",
  withdraw:         "Cerere retrasă",
  revoke:           "Acces revocat",
  expire:           "Acces expirat",
};
function StatusPill({ status }) {
  const m = STATUS_META[status] || { label: status, fg: "#374151", bg: "#f4f4f5" };
  return (
    <span className="inline-flex items-center px-2.5 h-6 text-[11px] font-medium rounded-md"
      style={{ background: m.bg, color: m.fg }}>{m.label}</span>
  );
}
function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? iso + "T00:00:00" : iso);
  if (isNaN(d)) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
}
const AVATAR_COLORS = [
  { bg: "#EEF2F7", fg: "#3d68b1" }, { bg: "#F4EFE9", fg: "#8B6E4A" },
  { bg: "#EEF3EF", fg: "#3F6B4E" }, { bg: "#F5EEF0", fg: "#A0546C" },
];
function initials(n = "") { return n.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]?.toUpperCase() || "").join(""); }
function colorFor(s = "") { let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) & 0xffffffff; return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length]; }
function Avatar({ name, size = 40 }) {
  const c = colorFor(name || "?");
  return (
    <div className="flex items-center justify-center font-medium shrink-0 rounded-lg"
      style={{ width: size, height: size, background: c.bg, color: c.fg, fontSize: size * 0.35 }}>
      {initials(name) || "?"}
    </div>
  );
}


export default function AccessApprovals() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/access-requests/pending");
      setItems(data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    return {
      manager: items.filter(i => i.status === "submitted"),
      pm: items.filter(i => i.status === "manager_approved"),
    };
  }, [items]);

  return (
    <PortalLayout variant="staff">
      <div className="max-w-[1200px] mx-auto">
        <header className="pb-10 pt-2">
          <div className="text-[12px] font-display font-medium text-ink-muted mb-3">
            Aprobări
          </div>
          <h1 className="text-[38px] md:text-[46px] leading-[1.05] font-display font-semibold tracking-[-0.02em] text-ink">
            Cereri de acces
          </h1>
          <p className="mt-4 text-[15px] leading-relaxed text-ink-muted max-w-xl">
            Aprobă sau respinge cererile de acces la proiecte ale echipei tale.
          </p>
        </header>

        {loading ? (
          <div className="py-24 flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-ink-muted" />
          </div>
        ) : items.length === 0 ? (
          <div className="py-24 text-center border-t border-neutral-200 text-ink-muted">
            <CheckCircle2 className="h-8 w-8 mx-auto text-emerald-500 mb-3" />
            <div className="text-[15px]">Nu ai cereri de aprobat momentan.</div>
          </div>
        ) : (
          <div className="space-y-10">
            {grouped.manager.length > 0 && (
              <Group title="Ca Manager Superior" subtitle="Aprobarea activează accesul"
                items={grouped.manager} onSelect={setSelected} />
            )}
            {grouped.pm.length > 0 && (
              <Group title="Cereri din fluxul vechi" subtitle="Aprobare finală — activează accesul"
                items={grouped.pm} onSelect={setSelected} />
            )}
          </div>
        )}
      </div>

      <ApprovalSheet open={!!selected} requestId={selected}
        onClose={() => setSelected(null)}
        onDone={async () => { setSelected(null); await load(); }} />
    </PortalLayout>
  );
}


function Group({ title, subtitle, items, onSelect }) {
  return (
    <section>
      <div className="mb-4 border-t border-neutral-200 pt-6">
        <div className="text-[15px] font-medium text-ink">{title}</div>
        <div className="text-[13px] text-ink-muted mt-0.5">{subtitle} · {items.length} cerere{items.length !== 1 ? "i" : ""}</div>
      </div>
      {items.map(r => (
        <button key={r.id} onClick={() => onSelect(r.id)}
          className="w-full flex items-center gap-4 py-4 border-b border-neutral-100 hover:bg-neutral-50 text-left">
          <Avatar name={r.employee?.name} size={44} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2.5 mb-1">
              <div className="text-[15px] font-medium truncate">
                {r.employee?.name} · {r.project?.code}
              </div>
              <StatusPill status={r.status} />
            </div>
            <div className="text-[13px] text-ink-muted truncate">
              Rol: {r.requested_role} · {r.project?.name}
            </div>
          </div>
          <ChevronRight className="h-4 w-4 text-neutral-300" />
        </button>
      ))}
    </section>
  );
}


function ApprovalSheet({ open, requestId, onClose, onDone }) {
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [comment, setComment] = useState("");

  useEffect(() => {
    if (!open || !requestId) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/access-requests/mine`).catch(() => ({ data: [] }));
        // Fallback: fetch pending and find
        const { data: pd } = await api.get("/access-requests/pending");
        const found = pd.find(r => r.id === requestId) || data.find(r => r.id === requestId);
        setRequest(found);
      } catch (e) { toast.error(apiErr(e)); }
      finally { setLoading(false); }
    })();
  }, [open, requestId]);

  useEffect(() => { document.body.style.overflow = open ? "hidden" : ""; return () => { document.body.style.overflow = ""; }; }, [open]);

  async function runAction(url, msg) {
    setBusy(url);
    try {
      await api.post(url, { comment });
      toast.success(msg);
      setDialog(null); setComment("");
      onDone();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(null); }
  }

  if (!open) return null;
  const status = request?.status;
  const isManagerStage = status === "submitted";
  const isPMStage = status === "manager_approved";
  const approveUrl = isManagerStage
    ? `/access-requests/${requestId}/approve-manager`
    : `/access-requests/${requestId}/approve-pm`;

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 bg-ink/20 z-40" />
      <aside className="fixed top-0 right-0 bottom-0 z-50 w-full max-w-[720px] bg-white border-l border-neutral-200 flex flex-col">
        {loading || !request ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-ink-muted" />
          </div>
        ) : (
          <>
            <div className="px-8 pt-8 pb-6 border-b border-neutral-100 flex items-start justify-between gap-4">
              <div className="flex items-center gap-4 min-w-0">
                <Avatar name={request.employee?.name} size={56} />
                <div className="min-w-0">
                  <div className="text-[12px] font-display font-medium text-ink-muted mb-1">
                    {request.employee?.name} · {request.employee?.job_title}
                  </div>
                  <div className="text-[22px] font-display font-semibold text-ink truncate">
                    {request.project?.code} — {request.project?.name}
                  </div>
                  <div className="mt-2 flex items-center gap-2 text-[13px] text-ink-muted">
                    <KeyRound className="h-3.5 w-3.5" />
                    Rol solicitat: <span className="font-medium text-ink">{request.requested_role}</span>
                  </div>
                </div>
              </div>
              <button onClick={onClose}
                className="h-10 w-10 flex items-center justify-center text-ink-muted hover:text-ink hover:bg-neutral-100 rounded-lg">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-8 py-8 space-y-8">
              <section>
                <div className="text-[12px] font-display font-medium text-ink-muted mb-3">Motiv</div>
                <div className="text-[14px] text-ink">{request.reason}</div>
              </section>
              {(request.needed_from || request.needed_until) && (
                <section>
                  <div className="text-[12px] font-display font-medium text-ink-muted mb-3">Perioada</div>
                  <div className="text-[14px] text-ink">
                    {fmtDate(request.needed_from)} → {request.needed_until ? fmtDate(request.needed_until) : "nedefinit"}
                  </div>
                </section>
              )}
              <section>
                <div className="text-[12px] font-display font-medium text-ink-muted mb-3">Istoric</div>
                <div className="space-y-3">
                  {(request.history || []).map((h, i) => (
                    <div key={i} className="flex gap-4">
                      <div className="text-[11px] text-ink-muted tabular-nums min-w-[120px]">
                        {`${fmtDate(h.at)}, ${new Date(h.at).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" })}`}
                      </div>
                      <div>
                        <div className="text-[13px]"><span className="font-medium">{h.by_name}</span> · {ACCESS_ACTION_LABEL[h.action] || h.action}</div>
                        {h.comment && <div className="text-[12px] text-ink-muted">{h.comment}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            <div className="px-8 py-5 border-t border-neutral-100 flex items-center gap-3 justify-end">
              <Button variant="ghost" size="md" onClick={onClose}>Închide</Button>
              <Button variant="danger" size="md" icon={XCircle}
                onClick={() => setDialog({ action: "reject", label: "Respinge cererea", requireComment: true })}>
                Respinge
              </Button>
              <Button variant="primary" size="md" icon={CheckCircle2}
                onClick={() => setDialog({ action: "approve", label: "Aprobă și activează acces" })}>
                Aprobă și activează acces
              </Button>
            </div>
          </>
        )}
      </aside>

      {dialog && (
        <div className="fixed inset-0 z-[60] bg-ink/40 flex items-center justify-center p-4"
          onClick={() => setDialog(null)}>
          <div onClick={e => e.stopPropagation()}
            className="bg-white rounded-2xl max-w-md w-full p-8">
            <div className="text-[12px] font-display font-medium text-ink-muted mb-2">Confirmare</div>
            <div className="text-[20px] font-display font-semibold text-ink mb-4">{dialog.label}</div>
            <div className="text-[12px] font-medium text-ink-muted mb-2">
              Comentariu {dialog.requireComment && <span className="text-ink">*</span>}
            </div>
            <textarea rows={3} value={comment} onChange={e => setComment(e.target.value)}
              placeholder={dialog.requireComment ? "Motivul respingerii" : "Comentariu opțional"}
              className="w-full border border-neutral-200 rounded-lg p-3 text-[14px] outline-none focus:border-ink resize-none" />
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="ghost" size="md" onClick={() => setDialog(null)}>Anulează</Button>
              <Button variant="primary" size="md"
                disabled={dialog.requireComment && !comment.trim()}
                loading={busy !== null}
                onClick={() => runAction(
                  dialog.action === "approve" ? approveUrl : `/access-requests/${requestId}/reject`,
                  dialog.action === "approve" ? "Cerere aprobată" : "Cerere respinsă"
                )}>
                Confirmă
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
